// frontend/src/features/landingPages/components/StructurePreview.js
import React from 'react';
import { getSectionType, FORM_FIELD_OPTIONS } from '../sectionCatalog';

/**
 * Renders a styled preview of the page STRUCTURE in a "nexa"-inspired
 * aesthetic (dark/light theme, lime accent, large display headings, pill
 * buttons). This is a design preview — not a hosted or production rendering.
 *
 * Supports:
 *  - device toggle: desktop | phone (changes preview frame width)
 *  - theme toggle:  dark | light
 */

function splitLines(value) {
  return (value || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Hero/CTA headings in the reference render the first line in the base text
 * color and the second line in the lime accent. We approximate that by
 * splitting the heading on a sentence boundary / newline.
 */
function renderAccentHeading(text) {
  const parts = String(text || '').split('\n');
  if (parts.length >= 2) {
    return (
      <>
        <span>{parts[0]}</span>
        <span className="lpx-accent-text"> {parts.slice(1).join(' ')}</span>
      </>
    );
  }
  // Fall back to splitting on the first period for a two-tone effect.
  const m = String(text || '').match(/^(.*?[.])\s*(.+)$/);
  if (m) {
    return (
      <>
        <span>{m[1]}</span>
        <span className="lpx-accent-text"> {m[2]}</span>
      </>
    );
  }
  return <span>{text}</span>;
}

function Eyebrow({ children }) {
  return (
    <div className="lpx-eyebrow">
      <span className="lpx-eyebrow-dash" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

function renderSectionBody(section) {
  const { type, content } = section;
  switch (type) {
    case 'hero':
      return (
        <div className="lpx-hero">
          <Eyebrow>Independent thinking. Extraordinary outcomes.</Eyebrow>
          <h1 className="lpx-display">{renderAccentHeading(content.heading)}</h1>
          {content.subheading && <p className="lpx-lead">{content.subheading}</p>}
          <div className="lpx-btn-row">
            {content.ctaLabel && (
              <span className="lpx-pill lpx-pill-primary">
                {content.ctaLabel}
                <i
                  className="fas fa-arrow-up-right-from-square lpx-pill-arrow"
                  aria-hidden="true"
                />
              </span>
            )}
            <span className="lpx-pill lpx-pill-ghost">
              <i className="far fa-comment" aria-hidden="true" />
              Talk to our AI
            </span>
          </div>
        </div>
      );

    case 'features': {
      const items = splitLines(content.items);
      const icons = ['fas fa-desktop', 'fas fa-bolt', 'fas fa-chart-line', 'fas fa-arrow-trend-up'];
      return (
        <div className="lpx-features">
          <Eyebrow>Built around your ambitions</Eyebrow>
          {content.heading && <h2 className="lpx-h2">{renderAccentHeading(content.heading)}</h2>}
          <div className="lpx-feature-grid">
            {items.length ? (
              items.map((it, i) => (
                <div className="lpx-feature-col" key={i}>
                  <div className="lpx-feature-top">
                    <span className="lpx-feature-num">/{String(i + 1).padStart(2, '0')}</span>
                    <i
                      className={`${icons[i % icons.length]} lpx-feature-icon`}
                      aria-hidden="true"
                    />
                  </div>
                  <div className="lpx-feature-title">{it}</div>
                </div>
              ))
            ) : (
              <div className="lpx-empty">No items yet</div>
            )}
          </div>
        </div>
      );
    }

    case 'faq': {
      const items = splitLines(content.items);
      return (
        <div className="lpx-faq">
          {content.heading && <h2 className="lpx-h2">{content.heading}</h2>}
          <ul className="lpx-faq-list">
            {items.length ? (
              items.map((it, i) => (
                <li className="lpx-faq-item" key={i}>
                  <span>{it}</span>
                  <i className="fas fa-plus" aria-hidden="true" />
                </li>
              ))
            ) : (
              <li className="lpx-empty">No questions yet</li>
            )}
          </ul>
        </div>
      );
    }

    case 'leadForm': {
      const fields = splitLines(content.fields);
      return (
        <div className="lpx-form-section">
          <div className="lpx-form-left">
            <Eyebrow>This is where it starts</Eyebrow>
            <h2 className="lpx-display lpx-display-sm">
              {renderAccentHeading(
                content.heading || 'A little conversation.\nA lot of potential.'
              )}
            </h2>
            <span className="lpx-pill lpx-pill-ghost">
              Rather start with a chat?
              <i className="far fa-comment" aria-hidden="true" />
            </span>
          </div>
          <div className="lpx-form-right">
            {fields.length ? (
              fields.map((f, i) => {
                const isSubject = f.trim().toLowerCase() === 'subject';
                const optDef = FORM_FIELD_OPTIONS.find(
                  (o) =>
                    o.label.toLowerCase() === f.trim().toLowerCase() ||
                    o.key === f.trim().toLowerCase()
                );
                if (isSubject || (optDef && optDef.type === 'select')) {
                  const label = optDef ? optDef.label : f;
                  const options = (optDef && optDef.options) || [
                    'Request a Demo',
                    'Sales & Pricing',
                    'Product Information',
                    'Partnership Opportunity',
                    'General Enquiry',
                  ];
                  return (
                    <div className="lpx-select-wrap" key={i}>
                      <select className="lpx-input lpx-select" defaultValue="" aria-label={label}>
                        <option value="" disabled>
                          {`-- Select ${label.toLowerCase()} --`}
                        </option>
                        {options.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      <i className="fas fa-chevron-down lpx-select-caret" aria-hidden="true" />
                    </div>
                  );
                }
                return (
                  <div className="lpx-input" key={i}>
                    <span>{f}</span>
                  </div>
                );
              })
            ) : (
              <div className="lpx-empty">No fields yet</div>
            )}
            {content.consentNote && (
              <label className="lpx-consent">
                <span className="lpx-checkbox" aria-hidden="true" />
                <span>{content.consentNote}</span>
              </label>
            )}
            <span className="lpx-pill lpx-pill-primary lpx-pill-block">
              Let&apos;s make it happen
              <i className="fas fa-arrow-up-right-from-square lpx-pill-arrow" aria-hidden="true" />
            </span>
          </div>
        </div>
      );
    }

    case 'testimonial':
      return (
        <div className="lpx-testimonial">
          <div className="lpx-quote">“{content.quote}”</div>
          <div className="lpx-attr">— {content.attribution}</div>
        </div>
      );

    case 'cta':
      return (
        <div className="lpx-cta">
          <h2 className="lpx-h2">{renderAccentHeading(content.heading)}</h2>
          {content.ctaLabel && (
            <span className="lpx-pill lpx-pill-primary">
              {content.ctaLabel}
              <i className="fas fa-arrow-up-right-from-square lpx-pill-arrow" aria-hidden="true" />
            </span>
          )}
        </div>
      );

    case 'footer':
      return <div className="lpx-footer">{content.note}</div>;

    default:
      return null;
  }
}

export default function StructurePreview({
  pageTitle,
  sections,
  device = 'desktop',
  theme = 'dark',
  onDeviceChange,
  onThemeChange,
}) {
  const safeSlug = pageTitle
    ? encodeURIComponent(pageTitle.toLowerCase().trim().replace(/\s+/g, '-'))
    : 'demo-page';

  return (
    <section className="lp-preview" aria-label="Structure preview">
      <div className="lp-preview-top-bar">
        <div className="lp-preview-title-group">
          <span className="lp-preview-header-icon" aria-hidden="true">
            <i className="fas fa-eye"></i>
          </span>
          <div className="lp-preview-titles">
            <h2 className="lp-preview-heading">Live Preview</h2>
            <span className="lp-preview-subtitle">
              Interactive layout representation · {device === 'desktop' ? 'Desktop' : 'Mobile'} ·{' '}
              {theme === 'dark' ? 'Dark theme' : 'Light theme'}
            </span>
          </div>
        </div>

        {/* Preview controls: device + theme toggles */}
        <div className="lp-preview-controls">
          <div className="lp-toggle" role="group" aria-label="Preview device">
            <button
              type="button"
              className={`lp-toggle-btn ${device === 'desktop' ? 'active' : ''}`}
              onClick={() => onDeviceChange && onDeviceChange('desktop')}
              aria-pressed={device === 'desktop'}
              title="Desktop preview"
            >
              <i className="fas fa-desktop" aria-hidden="true" />
              <span>Desktop</span>
            </button>
            <button
              type="button"
              className={`lp-toggle-btn ${device === 'phone' ? 'active' : ''}`}
              onClick={() => onDeviceChange && onDeviceChange('phone')}
              aria-pressed={device === 'phone'}
              title="Phone preview"
            >
              <i className="fas fa-mobile-screen" aria-hidden="true" />
              <span>Phone</span>
            </button>
          </div>

          <div className="lp-toggle" role="group" aria-label="Preview theme">
            <button
              type="button"
              className={`lp-toggle-btn ${theme === 'dark' ? 'active' : ''}`}
              onClick={() => onThemeChange && onThemeChange('dark')}
              aria-pressed={theme === 'dark'}
              title="Dark mode"
            >
              <i className="fas fa-moon" aria-hidden="true" />
              <span>Dark</span>
            </button>
            <button
              type="button"
              className={`lp-toggle-btn ${theme === 'light' ? 'active' : ''}`}
              onClick={() => onThemeChange && onThemeChange('light')}
              aria-pressed={theme === 'light'}
              title="Light mode"
            >
              <i className="fas fa-sun" aria-hidden="true" />
              <span>Light</span>
            </button>
          </div>
        </div>
      </div>

      {/* Device frame */}
      <div className={`lp-device lp-device-${device}`}>
        {device === 'desktop' && (
          <div className="lp-browser-chrome" aria-hidden="true">
            <div className="lp-browser-dots">
              <span className="lp-browser-dot lp-dot-red" />
              <span className="lp-browser-dot lp-dot-yellow" />
              <span className="lp-browser-dot lp-dot-green" />
            </div>
            <div className="lp-browser-url-bar">
              <i className="fas fa-lock" />
              <span className="lp-browser-url">huntrix.app/p/{safeSlug}</span>
            </div>
          </div>
        )}
        {device === 'phone' && (
          <div className="lp-phone-notch" aria-hidden="true">
            <span className="lp-phone-speaker" />
            <span className="lp-phone-lens" />
          </div>
        )}
        <div className={`lpx-canvas lpx-theme-${theme}`}>
          {/* nexa-style top nav */}
          <div className="lpx-nav">
            <span className="lpx-brand">
              <span className="lpx-brand-mark">{'///'}</span> {pageTitle || 'untitled.'}
            </span>
            <span className="lpx-nav-cta">
              Let&apos;s connect
              <i className="fas fa-arrow-up-right-from-square" aria-hidden="true" />
            </span>
          </div>

          {sections.length === 0 ? (
            <div className="lpx-placeholder">
              <i className="fas fa-layer-group" aria-hidden="true" />
              <p>Your page structure will appear here. Add sections to begin.</p>
            </div>
          ) : (
            sections.map((section) => {
              const def = getSectionType(section.type);
              return (
                <div className={`lpx-section lpx-section-${section.type}`} key={section.id}>
                  <span className="lpx-tag">
                    <i className={def ? def.icon : 'fas fa-square'} aria-hidden="true" />
                    {section.label}
                  </span>
                  {renderSectionBody(section)}
                </div>
              );
            })
          )}
        </div>
      </div>
    </section>
  );
}
